import * as analytics from '@/lib/services/analytics';

type AuthMethod = 'email' | 'google' | 'facebook' | 'apple';
type UserRole = 'customer' | 'business_customer' | 'driver';

function track(fn: () => Promise<void>): void {
  void fn().catch((error) => {
    if (__DEV__) {
      console.debug('[analytics] event failed', error);
    }
  });
}

export function trackLogin(method: AuthMethod, role?: UserRole): void {
  track(async () => {
    await analytics.logEvent('login', { method });
    if (role) {
      await analytics.setUserProperties({ user_role: role });
    }
  });
}

export function trackSignUp(method: AuthMethod, role: UserRole): void {
  track(() => analytics.logEvent('sign_up', { method, user_role: role }));
}

export function trackSignOut(): void {
  track(() => analytics.logEvent('logout'));
}

export function trackBookingCreated(booking: {
  id: number;
  service_type: string;
  payment_method?: string;
}): void {
  track(() =>
    analytics.logEvent('booking_created', {
      booking_id: booking.id,
      service_type: booking.service_type,
      ...(booking.payment_method
        ? { payment_method: booking.payment_method }
        : {}),
    }),
  );
}

export function trackBookingAccepted(bookingId: number): void {
  track(() =>
    analytics.logEvent('booking_accepted', { booking_id: bookingId }),
  );
}

export function trackBookingRejected(bookingId: number): void {
  track(() =>
    analytics.logEvent('booking_rejected', { booking_id: bookingId }),
  );
}

export function trackBookingCancelled(bookingId: number): void {
  track(() =>
    analytics.logEvent('booking_cancelled', { booking_id: bookingId }),
  );
}

export function trackBookingArrived(bookingId: number): void {
  track(() =>
    analytics.logEvent('booking_arrived', { booking_id: bookingId }),
  );
}

export function trackBookingRideStarted(bookingId: number): void {
  track(() =>
    analytics.logEvent('booking_ride_started', { booking_id: bookingId }),
  );
}

export function trackBookingDelivered(bookingId: number): void {
  track(() =>
    analytics.logEvent('booking_delivered', { booking_id: bookingId }),
  );
}

export function trackBookingCompleted(bookingId: number): void {
  track(() =>
    analytics.logEvent('booking_completed', { booking_id: bookingId }),
  );
}

export function trackQuoteCreated(quote: {
  id: number;
  booking_id: number;
  amount: number;
}): void {
  track(() =>
    analytics.logEvent('quote_created', {
      quote_id: quote.id,
      booking_id: quote.booking_id,
      amount: quote.amount,
    }),
  );
}

export function trackQuoteAccepted(quote: {
  id: number;
  booking_id: number;
}): void {
  track(() =>
    analytics.logEvent('quote_accepted', {
      quote_id: quote.id,
      booking_id: quote.booking_id,
    }),
  );
}

export function trackQuoteRejected(quoteId: number, bookingId: number): void {
  track(() =>
    analytics.logEvent('quote_rejected', {
      quote_id: quoteId,
      booking_id: bookingId,
    }),
  );
}

export function trackQuoteCountered(quoteId: number, amount: number): void {
  track(() =>
    analytics.logEvent('quote_countered', {
      quote_id: quoteId,
      amount,
    }),
  );
}

export function trackDriverRegistered(): void {
  track(() => analytics.logEvent('driver_registered'));
}

export function trackRoleSwitched(role: UserRole): void {
  track(async () => {
    await analytics.logEvent('role_switched', { user_role: role });
    await analytics.setUserProperties({ user_role: role });
  });
}
